import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Chart } from 'chart.js';
import { SalesOverviewService } from './sales-overview.service';
import { ClientSalesOverviewDTO, SalesOverviewFilterDTO, ProductSalesTableRowDTO } from '../../../base/models/models.swagger.generated';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

@Component({
  selector: 'app-sales-overview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CardModule,
    ChartModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    CalendarModule,
    TagModule,
    ToastModule,
    TranslateModule
  ],
  templateUrl: './sales-overview.component.html',
  styleUrl: './sales-overview.component.scss'
})
export class SalesOverviewComponent implements OnInit, OnDestroy {
  @ViewChild('dashboardContent') dashboardContent!: ElementRef;
  overview: ClientSalesOverviewDTO | null = null;
  filterForm: FormGroup;
  loading: boolean = false;
  selectedDateRange: string = 'Last30Days';
  inventorySoldPercent: number = 0;
  deliveryPercent: number = 0;
  exporting: boolean = false;
  isPrintMode: boolean = false;
  disablePagination: boolean = false;
  private subscription = new Subscription();

  dateRangeOptions = [
    { label: 'Today', value: 'Today' },
    { label: 'Last 7 Days', value: 'Last7Days' },
    { label: 'Last 30 Days', value: 'Last30Days' },
    { label: 'Custom', value: 'Custom' }
  ];

  revenueChartOptions: any;
  inventoryChartOptions: any;
  deliveryChartOptions: any;
  orderStatusChartOptions: any;
  miniChartOptions: any;
  stockSoldChartData: any;
  deliveryRateChartData: any;
  growthChartData: any;

  constructor(
    private salesOverviewService: SalesOverviewService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.filterForm = this.fb.group({
      dateRangeType: ['Last30Days'],
      startDate: [null],
      endDate: [null],
      productName: ['']
    });
  }

  ngOnInit() {
    this.loadDashboard();
    this.initializeCharts();
  }

  private loadDashboard() {
    this.loading = true;
    const filters = this.buildFilters();
    
    this.subscription.add(
      this.salesOverviewService.getClientSalesOverview(filters, 0, 20).subscribe({
        next: (data) => {
          this.overview = data;
          this.updateCharts();
          this.cdr.detectChanges();
          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading dashboard:', err);
          this.loading = false;
        }
      })
    );
  }

  private buildFilters(): SalesOverviewFilterDTO {
    const formValue = this.filterForm.value;
    return new SalesOverviewFilterDTO({
      dateRangeType: formValue.dateRangeType,
      startDate: formValue.startDate,
      endDate: formValue.endDate,
      sellerId: formValue.sellerId,
      categoryId: formValue.categoryId,
      deliveryStatus: formValue.deliveryStatus,
      productName: formValue.productName
    });
  }

  private initializeCharts() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color') || '#333';
    const surfaceColor = documentStyle.getPropertyValue('--surface-border');

    const centerTextPlugin = {
      id: 'centerText',
      afterDatasetsDraw(chart: any) {
        if (!chart.data.centerText) return;
        const { width, height, ctx } = chart;
        ctx.save();
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = textColor;
        const lines = chart.data.centerText.split('\n');
        const lineHeight = 14;
        const startY = height / 2 - 10;
        lines.forEach((line: string, index: number) => {
          ctx.fillText(line, width / 2, startY + index * lineHeight);
        });
        ctx.restore();
      }
    };

    Chart.register(centerTextPlugin);

    this.miniChartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: { legend: { display: false } }
    };

    this.revenueChartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: { legend: { labels: { color: textColor } } },
      scales: {
        x: { ticks: { color: textColor }, grid: { color: surfaceColor } },
        y: { ticks: { color: textColor }, grid: { color: surfaceColor } }
      }
    };

    this.inventoryChartOptions = {
      maintainAspectRatio: true,
      responsive: false,
      plugins: { legend: { display: false }, centerText: {} }
    };

    this.deliveryChartOptions = {
      maintainAspectRatio: true,
      responsive: false,
      plugins: { legend: { display: false }, centerText: {} }
    };

    this.orderStatusChartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: { legend: { labels: { color: textColor } } },
      scales: {
        x: { ticks: { color: textColor }, grid: { color: surfaceColor } },
        y: { ticks: { color: textColor }, grid: { color: surfaceColor } }
      }
    };
  }

  private updateCharts() {
    if (!this.overview) return;

    // Stock Sold Chart
    this.stockSoldChartData = {
      datasets: [{
        data: [this.overview.stockSoldPercentage, 100 - this.overview.stockSoldPercentage],
        backgroundColor: ['#2196F3', '#E0E0E0'],
        borderColor: ['#2196F3', '#E0E0E0'],
        borderWidth: 0
      }]
    };

    // Delivery Rate Chart
    this.deliveryRateChartData = {
      datasets: [{
        data: [this.overview.deliveryPercentage, 100 - this.overview.deliveryPercentage],
        backgroundColor: ['#4CAF50', '#E0E0E0'],
        borderColor: ['#4CAF50', '#E0E0E0'],
        borderWidth: 0
      }]
    };

    // Growth Chart
    this.growthChartData = {
      datasets: [{
        data: [8, 92],
        backgroundColor: ['#4CAF50', '#E0E0E0'],
        borderColor: ['#4CAF50', '#E0E0E0'],
        borderWidth: 0
      }]
    };

    if (this.overview.revenueTrend?.length) {
      this.revenueChartOptions = {
        ...this.revenueChartOptions,
        data: {
          labels: this.overview.revenueTrend.map(r => r.label),
          datasets: [{
            label: 'Revenue',
            data: this.overview.revenueTrend.map(r => r.amount),
            borderColor: '#2196F3',
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            tension: 0.4,
            fill: true
          }]
        }
      };
    }

    const invPerf = this.overview.inventoryPerformance;
    if (invPerf?.totalSold || invPerf?.remainingStock) {
      const total = (invPerf.totalSold || 0) + (invPerf.remainingStock || 0);
      this.inventorySoldPercent = total > 0 ? Math.round(((invPerf.totalSold || 0) / total) * 100) : 0;
      this.inventoryChartOptions = {
        ...this.inventoryChartOptions,
        data: {
          labels: ['Sold', 'In Stock'],
          datasets: [{
            data: [Math.max(0, invPerf.totalSold), invPerf.remainingStock],
            backgroundColor: ['#2196F3', '#FF9800'],
            borderColor: ['#2196F3', '#FF9800'],
            borderWidth: 1
          }],
          centerText: `In Stock\n${invPerf.remainingStock}`
        }
      };
    }

    const delPerf = this.overview.deliveryPerformance;
    if (delPerf?.totalOrderItems) {
      this.deliveryPercent = delPerf.totalOrderItems > 0 ? Math.round((delPerf.deliveredItems / delPerf.totalOrderItems) * 100) : 0;
      this.deliveryChartOptions = {
        ...this.deliveryChartOptions,
        data: {
          labels: ['Delivered', 'Pending'],
          datasets: [{
            data: [delPerf.deliveredItems, delPerf.totalOrderItems - delPerf.deliveredItems],
            backgroundColor: ['#4CAF50', '#2196F3'],
            borderColor: ['#4CAF50', '#2196F3'],
            borderWidth: 1
          }],
          centerText: `Delivered\n${delPerf.deliveredItems}`
        }
      };
    }

    const orderStatus = this.overview.orderStatusBreakdown;
    if (orderStatus) {
      this.orderStatusChartOptions = {
        ...this.orderStatusChartOptions,
        data: {
          labels: ['Pending', 'Confirmed', 'Delivered', 'Cancelled'],
          datasets: [{
            label: 'Order Count',
            data: [orderStatus.pendingCount, orderStatus.confirmedCount, orderStatus.deliveredCount, orderStatus.cancelledCount],
            backgroundColor: ['#FF9800', '#2196F3', '#4CAF50', '#F44336'],
            borderColor: ['#FF9800', '#2196F3', '#4CAF50', '#F44336'],
            borderWidth: 1
          }]
        }
      };
    }
  }

  onFilterChange() {
    this.loadDashboard();
  }

  onPageChange(event: any) {
    const filters = this.buildFilters();
    this.subscription.add(
      this.salesOverviewService.getClientSalesOverview(filters, event.first, event.rows).subscribe({
        next: (data) => {
          this.overview = data;
          this.cdr.detectChanges();
        },
        error: (err) => console.error('Error loading page:', err)
      })
    );
  }

  getAlertSeverityClass(severity: string | undefined): 'success' | 'secondary' | 'info' | 'warning' | 'danger' | 'contrast' {
    switch (severity) {
      case 'Red': return 'danger';
      case 'Orange': return 'warning';
      default: return 'info';
    }
  }

  getSalePercentageClass(percentage: number | undefined): 'success' | 'secondary' | 'info' | 'warning' | 'danger' | 'contrast' {
    if (!percentage) return 'info';
    if (percentage < 50) return 'danger';
    if (percentage < 75) return 'warning';
    return 'success';
  }

  getDeliveryPercentageClass(percentage: number | undefined): 'success' | 'secondary' | 'info' | 'warning' | 'danger' | 'contrast' {
    if (!percentage) return 'info';
    if (percentage < 50) return 'danger';
    if (percentage < 75) return 'warning';
    return 'success';
  }

  getDaysToExpireClass(days: number | undefined): 'success' | 'secondary' | 'info' | 'warning' | 'danger' | 'contrast' {
    if (!days || days === 2147483647) return 'info';
    if (days < 0) return 'danger';
    if (days < 3) return 'danger';
    if (days < 7) return 'warning';
    return 'success';
  }

  getProductUOM(productId: number): string {
    const product = this.overview?.productsTable?.items?.find(p => p.productId === productId);
    return product?.unitOfMeasure || '';
  }

  async exportDashboard() {
    this.exporting = true;
    this.isPrintMode = true;
    this.disablePagination = true;
    this.cdr.detectChanges();
    
    try {
      const filters = this.buildFilters();
      const allProducts: any[] = [];
      let skip = 0;
      const take = 1000;
      let hasMore = true;
      
      while (hasMore) {
        await new Promise(resolve => {
          this.subscription.add(
            this.salesOverviewService.getClientSalesOverview(filters, skip, take).subscribe({
              next: (data) => {
                if (data.productsTable?.items?.length) {
                  allProducts.push(...data.productsTable.items);
                  if (data.productsTable.items.length < take) {
                    hasMore = false;
                  }
                  if (!this.overview) this.overview = data;
                  this.overview.productsTable.items = allProducts;
                } else {
                  hasMore = false;
                }
                this.cdr.detectChanges();
                setTimeout(resolve, 100);
              },
              error: (err) => {
                console.error('Error loading products:', err);
                hasMore = false;
                resolve(null);
              }
            })
          );
        });
        skip += take;
      }
      
      const element = this.dashboardContent.nativeElement;
      const button = element.querySelector('button[title="Export Dashboard"]');
      if (button) button.style.display = 'none';
      
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = 297;
      const pageHeight = 210;
      const margin = 5;
      const padding = 2;
      const contentWidth = pageWidth - (2 * margin) - (2 * padding);
      let currentY = margin + padding;
      
      const sections = [
        '.header-section',
        '.kpi-cards-grid',
        '.main-content-grid',
        '.performance-grid',
        '.top-products-card:last-of-type',
        '.data-table'
      ];
      
      for (const selector of sections) {
        const sectionEl = element.querySelector(selector);
        if (!sectionEl) continue;
        
        const canvas = await html2canvas(sectionEl, {
          scale: 2,
          useCORS: true,
          logging: false,
          allowTaint: true,
          windowHeight: sectionEl.scrollHeight
        });
        
        const sectionHeight = (canvas.height * contentWidth) / canvas.width;
        const availableSpace = pageHeight - currentY - margin;
        
        if (sectionHeight > availableSpace && currentY > margin + padding) {
          pdf.setDrawColor(0);
          pdf.setLineWidth(0.5);
          pdf.rect(margin, margin, pageWidth - (2 * margin), pageHeight - (2 * margin));
          pdf.addPage();
          currentY = margin + padding;
        }
        
        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', margin + padding, currentY, contentWidth, sectionHeight);
        currentY += sectionHeight + 2;
      }
      
      pdf.setDrawColor(0);
      pdf.setLineWidth(0.5);
      pdf.rect(margin, margin, pageWidth - (2 * margin), pageHeight - (2 * margin));
      
      if (button) button.style.display = '';
      pdf.save(`sales-overview-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting dashboard:', error);
    } finally {
      this.isPrintMode = false;
      this.exporting = false;
      this.disablePagination = false;
      this.loadDashboard();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
